import { AI_PROVIDERS, AI_MODELS } from '@ellie/shared';
import type { AIProviderCode } from '@ellie/shared';
import { useStudioStore } from '@/stores/useStudioStore';
import { GitCompareArrows } from 'lucide-react';

export function ProviderSelector() {
  const {
    provider,
    model,
    setProvider,
    setModel,
    compareEnabled,
    compareProvider,
    compareModel,
    setCompareEnabled,
    setCompareProvider,
    setCompareModel,
  } = useStudioStore();

  const primaryModels = AI_MODELS[provider] ?? [];
  const compareModels = AI_MODELS[compareProvider] ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold uppercase tracking-wider text-text-muted">
          AI Provider
        </label>
        <button
          onClick={() => setCompareEnabled(!compareEnabled)}
          className={`flex items-center gap-1 rounded px-2 py-1 text-xs transition-colors ${
            compareEnabled
              ? 'bg-brand-light text-brand-dark'
              : 'text-text-muted hover:text-text-primary'
          }`}
          title="Toggle comparison mode"
        >
          <GitCompareArrows size={12} />
          Compare
        </button>
      </div>

      {/* Primary provider/model */}
      <div className="grid grid-cols-2 gap-2">
        <select
          value={provider}
          onChange={(e) => setProvider(e.target.value as AIProviderCode)}
          className="rounded-lg border border-border px-2 py-1.5 text-xs text-text-primary outline-none transition-colors focus:border-brand focus:ring-1 focus:ring-brand"
        >
          {AI_PROVIDERS.map((p) => (
            <option key={p.code} value={p.code}>
              {p.name}
            </option>
          ))}
        </select>

        <select
          value={model}
          onChange={(e) => setModel(e.target.value)}
          className="rounded-lg border border-border px-2 py-1.5 text-xs text-text-primary outline-none transition-colors focus:border-brand focus:ring-1 focus:ring-brand"
        >
          {primaryModels.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
      </div>

      {/* Comparison provider/model (visible when compare is enabled) */}
      {compareEnabled && (
        <div className="rounded-lg border border-dashed border-brand/40 bg-brand-light/30 p-2">
          <p className="mb-2 text-xs font-medium text-brand-dark">Compare with:</p>
          <div className="grid grid-cols-2 gap-2">
            <select
              value={compareProvider}
              onChange={(e) => setCompareProvider(e.target.value as AIProviderCode)}
              className="rounded-lg border border-border px-2 py-1.5 text-xs text-text-primary outline-none transition-colors focus:border-brand focus:ring-1 focus:ring-brand"
            >
              {AI_PROVIDERS.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.name}
                </option>
              ))}
            </select>

            <select
              value={compareModel}
              onChange={(e) => setCompareModel(e.target.value)}
              className="rounded-lg border border-border px-2 py-1.5 text-xs text-text-primary outline-none transition-colors focus:border-brand focus:ring-1 focus:ring-brand"
            >
              {compareModels.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>
          <p className="mt-1.5 text-[10px] text-text-light">
            Regenerate a block to see side-by-side results from both providers.
          </p>
        </div>
      )}
    </div>
  );
}
